import { cn } from '@/lib/utils';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { ReactNode, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { SidebarTooltip } from './SidebarRow';

interface SidebarProps {
    brandContent: ReactNode;
    children: ReactNode;
    userCard?: ReactNode;
    className?: string;
    // When true, also render the sidebar as a fixed-position drawer below lg
    // (used for the mobile slide-in). Undefined means desktop-only.
    mobileOpen?: boolean;
    /**
     * True when the sidebar is the 48px icon rail. The mobile drawer ignores
     * it and always shows the labels.
     */
    collapsed?: boolean;
    /** Toggle the rail. When absent, the sidebar shows no toggle button. */
    onToggleCollapsed?: () => void;
    /** The keyboard shortcut of the toggle, for its tooltip (for example "⌘\"). */
    shortcutLabel?: string;
}

const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
    {
        brandContent,
        children,
        userCard,
        className,
        mobileOpen,
        collapsed = false,
        onToggleCollapsed,
        shortcutLabel,
    },
    ref,
) {
    // Scroll-position shadows: the sticky brand/role block gains a bottom shadow
    // once the nav scrolls down from the top, and the pinned user card gains a
    // top shadow while nav content is still hidden below it. Both read as
    // elevated above the scrolling list.
    const navRef = useRef<HTMLElement>(null);
    const [navScrolled, setNavScrolled] = useState(false);
    const [navAtEnd, setNavAtEnd] = useState(true);
    // The drawer below lg always shows the labels.
    const rail = collapsed && !mobileOpen;

    const updateScrollShadows = useCallback(() => {
        const el = navRef.current;
        if (!el) return;
        setNavScrolled(el.scrollTop > 0);
        setNavAtEnd(el.scrollHeight - el.scrollTop - el.clientHeight <= 1);
    }, []);

    useEffect(() => {
        const el = navRef.current;
        if (!el) return;
        updateScrollShadows();
        const ro = new ResizeObserver(updateScrollShadows);
        ro.observe(el);
        return () => ro.disconnect();
    }, [updateScrollShadows]);

    const toggleLabel = rail ? 'Expand sidebar' : 'Collapse sidebar';

    return (
        <aside
            ref={ref}
            data-collapsed={rail ? 'true' : undefined}
            className={cn(
                'h-full flex-shrink-0 flex-col bg-[rgb(var(--color-surface-secondary))] border-r border-[rgb(var(--color-divider))]',
                'shadow-[inset_-1px_0_0_rgba(255,255,255,0.74)]',
                'transition-[width] duration-[120ms] ease-out motion-reduce:transition-none',
                rail ? 'w-12' : 'w-sidebar',
                // Desktop: always visible inline
                'lg:flex lg:static lg:z-auto lg:shadow-none',
                // Mobile: only visible when explicitly opened, as fixed-position drawer
                mobileOpen ? 'flex fixed inset-y-0 left-0 z-50 shadow-2xl' : 'hidden',
                className,
            )}
        >
            <div
                className={cn(
                    'pt-2 pb-1 transition-shadow duration-card-hover',
                    rail ? 'px-1' : 'px-2',
                    navScrolled && 'shadow-[0_4px_8px_-4px_rgba(0,0,0,0.12)]',
                )}
            >
                {brandContent}
            </div>

            <nav
                ref={navRef}
                onScroll={updateScrollShadows}
                className={cn(
                    'flex-1 overflow-y-auto overflow-x-hidden pb-4 pt-1',
                    rail ? 'px-1' : 'px-2',
                )}
            >
                {children}
            </nav>

            {(userCard || onToggleCollapsed) && (
                <div
                    className={cn(
                        'flex border-t border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface)/0.7)] py-1.5 transition-shadow duration-card-hover',
                        rail ? 'flex-col items-center gap-1 px-1' : 'items-center gap-1 px-2',
                        !navAtEnd && 'shadow-[0_-4px_8px_-4px_rgba(0,0,0,0.12)]',
                    )}
                >
                    {userCard && <div className={cn('min-w-0', !rail && 'flex-1')}>{userCard}</div>}
                    {onToggleCollapsed && (
                        <button
                            type="button"
                            onClick={onToggleCollapsed}
                            aria-label={toggleLabel}
                            aria-expanded={!rail}
                            aria-keyshortcuts={shortcutLabel ? 'Meta+\\ Control+\\ [' : undefined}
                            className={cn(
                                // The drawer below lg has no rail, so it has no toggle.
                                'group hidden h-6 w-6 flex-shrink-0 items-center justify-start rounded-[6px] pl-1 lg:flex',
                                'text-[rgb(var(--color-text-tertiary))] hover:bg-[rgb(var(--color-text-primary)/0.06)] hover:text-[rgb(var(--color-text-primary))]',
                                'outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]',
                            )}
                        >
                            {rail ? (
                                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
                            )}
                            {/* The content of the button starts 4px from its left
                                edge. In the rail the button is at x=12, so a 38px
                                shift puts the tooltip 6px to the right of the rail.
                                In the panel a 32px shift puts it 12px to the right
                                of the button, clear of the border. */}
                            <SidebarTooltip
                                className={rail ? 'translate-x-[38px]' : 'translate-x-[32px]'}
                            >
                                <span>{toggleLabel}</span>
                                {shortcutLabel && (
                                    <kbd className="font-sans text-[11px] text-[rgb(var(--color-text-tertiary))]">
                                        {shortcutLabel}
                                    </kbd>
                                )}
                            </SidebarTooltip>
                        </button>
                    )}
                </div>
            )}
        </aside>
    );
});

export default Sidebar;

interface SidebarMenuProps {
    /** The trigger. It gets the open state and the toggle function. */
    renderTrigger: (state: { open: boolean; toggle: () => void }) => ReactNode;
    /** The panel content. It gets a function that closes the panel. */
    children: (close: () => void) => ReactNode;
    /** The position and the width of the panel, relative to the trigger. */
    panelClassName: string;
    /** The accessible name of the panel. */
    label: string;
    className?: string;
}

/**
 * A small popover for the sidebar chrome: the view switcher in the rail and
 * the user menu. A click outside the panel or the Escape key closes it.
 */
export function SidebarMenu({
    renderTrigger,
    children,
    panelClassName,
    label,
    className,
}: SidebarMenuProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const close = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((prev) => !prev), []);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setOpen(false);
            rootRef.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div ref={rootRef} className={cn('relative', className)}>
            {renderTrigger({ open, toggle })}
            {open && (
                <div
                    role="dialog"
                    aria-label={label}
                    className={cn(
                        'absolute z-50 flex flex-col gap-1 rounded-[8px] p-1.5',
                        'border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-raised))] shadow-lg',
                        panelClassName,
                    )}
                >
                    {children(close)}
                </div>
            )}
        </div>
    );
}
